import cx from 'clsx';

import {
  InputProps,
  InputWrapperProps,
  MantineSize,
  MantineTheme,
  NumberInputProps,
  rem
} from '@mantine/core';

import { TextInputProps } from '..';
import { InputStyleProps } from '../types';

import classes from './TextInput.module.css';

interface InputSizeProps {
  height: number;
  fontSize: number;
}

export const DefaultInputProps = {
  size: 'xl',
  radius: 6
};

export const InputOverrideClassNames = (props: InputStyleProps) => {
  const { size, focussed, error, rightSection, enableRightSectionEvents } = props;

  return {
    label: cx(classes.label),
    section: cx({
      [classes.sectionError]: !!error,
      [classes.sectionFocussed]: focussed,
      [classes.rightWithEvents]: enableRightSectionEvents,
      [classes.sectionXs]: size === 'xs',
      [classes.sectionSm]: size === 'sm',
      [classes.sectionMd]: size === 'md',
      [classes.sectionLg]: size === 'lg',
      [classes.sectionXl]: size === 'xl'
    }),
    input: cx(classes.input, {
      [classes.clearPr]: !rightSection,
      [classes.inputXs]: size === 'xs',
      [classes.inputSm]: size === 'sm',
      [classes.inputMd]: size === 'md',
      [classes.inputLg]: size === 'lg',
      [classes.inputXl]: size === 'xl'
    }),
    error: cx(classes.error)
  };
};

// we should stick with one of the approaches, either write classes using classNames, or use the vars and styles and write all relevant styles here.
// using var resolvers for some properties is recommended, as mantine tries to determine inputSizes based on theses vars. But we are limited by the number of the vars we can use.
export const inputSizes: Partial<Record<MantineSize, InputSizeProps>> = {
  xs: {
    height: 20,
    fontSize: 10
  },
  sm: {
    height: 24,
    fontSize: 12
  },
  md: {
    height: 28,
    fontSize: 12
  },
  lg: {
    height: 32,
    fontSize: 13
  },
  xl: {
    height: 40,
    fontSize: 13
  }
};

export const InputVarsResolver = (
  theme: MantineTheme,
  props: TextInputProps | InputProps | NumberInputProps
): any => {
  if (props.size === 'xs') {
    return {
      wrapper: {
        '--input-fz': rem(inputSizes?.xs?.fontSize),
        '--input-height': rem(inputSizes?.xs?.height),
        '--input-left-section-width': rem(26),
        '--input-right-section-width': rem(26)
      }
    };
  } else if (props.size === 'sm') {
    return {
      wrapper: {
        '--input-fz': rem(inputSizes?.sm?.fontSize),
        '--input-height': rem(inputSizes?.sm?.height),
        '--input-left-section-width': rem(26),
        '--input-right-section-width': rem(26)
      }
    };
  } else if (props.size === 'md') {
    return {
      wrapper: {
        '--input-fz': rem(inputSizes?.md?.fontSize),
        '--input-height': rem(inputSizes?.md?.height),
        '--input-left-section-width': rem(28),
        '--input-right-section-width': rem(28)
      }
    };
  } else if (props.size === 'lg') {
    return {
      wrapper: {
        '--input-fz': rem(inputSizes?.lg?.fontSize),
        '--input-height': rem(inputSizes?.lg?.height),
        '--input-left-section-width': rem(34),
        '--input-right-section-width': rem(34)
      }
    };
  } else if (props.size === 'xl') {
    return {
      wrapper: {
        '--input-fz': rem(inputSizes?.xl?.fontSize),
        '--input-height': rem(inputSizes?.xl?.height),
        '--input-left-section-width': rem(36),
        '--input-right-section-width': rem(36)
      }
    };
  } else {
    return {
      wrapper: {
        '--input-fz': rem(inputSizes?.md?.fontSize),
        '--input-height': rem(inputSizes?.md?.height),
        '--input-left-section-width': rem(28),
        '--input-right-section-width': rem(28)
      }
    };
  }
};

export const InputWrapperOverrideClassNames = () =>
  // theme: MantineTheme,
  // props: InputWrapperProps
  {
    return {
      label: classes.label,
      error: classes.error
    };
  };

export const InputWrapperVarsResolver = (
  theme: MantineTheme,
  props: InputWrapperProps
): any => {
  if (props.size === 'xs') {
    return {
      label: {
        '--input-label-size': rem(12)
      },
      error: {
        '--input-error-size': rem(12)
      },
      description: {
        '--input-description-size': rem(8)
      }
    };
  } else if (props.size === 'sm') {
    return {
      label: {
        '--input-label-size': rem(13)
      },
      error: {
        '--input-error-size': rem(12)
      },
      description: {
        '--input-description-size': rem(9)
      }
    };
  } else if (props.size === 'md') {
    return {
      label: {
        '--input-label-size': rem(13)
      },
      error: {
        '--input-error-size': rem(12)
      },
      description: {
        '--input-description-size': rem(9)
      }
    };
  } else if (props.size === 'lg') {
    return {
      label: {
        '--input-label-size': rem(14)
      },
      error: {
        '--input-error-size': rem(13)
      },
      description: {
        '--input-description-size': rem(10)
      }
    };
  } else if (props.size === 'xl') {
    return {
      label: {
        '--input-label-size': rem(14)
      },
      error: {
        '--input-error-size': rem(13)
      },
      description: {
        '--input-description-size': rem(10)
      }
    };
  } else {
    return {
      label: {
        '--input-label-size': rem(13)
      },
      error: {
        '--input-error-size': rem(12)
      }
    };
  }
};
